import type { Workbook } from 'exceljs';
import type {
  DynamicTableColumnConfig,
  DynamicTableRowConfig,
  DynamicTableRowHeaderInputConfig,
} from '../config/types';
import { getExcelTemplateFilename } from './excel-template-filename';
import { interpolateQuestionText } from './form-utils';

export type DynamicTableExcelOptions = {
  title: string;
  rowHeader: string;
  rows: DynamicTableRowConfig[];
  columns: DynamicTableColumnConfig[];
  values: Record<string, string>;
  interpolationValues: Record<string, unknown>;
  continuous?: boolean;
  rowHeaderInput?: DynamicTableRowHeaderInputConfig;
  showRowHeader?: boolean;
};

export type DynamicTablesExcelOptions = {
  title: string;
  tables: DynamicTableExcelOptions[];
};

const HEADER_FILL = 'D9EAF7';
const ROW_HEADER_FILL = 'F3F2F1';
const BORDER_COLOUR = 'B1B4B6';

const withEditableCellsOnly = (options: DynamicTableExcelOptions): DynamicTableExcelOptions => ({
  ...options,
  rows: options.rows.filter((row) => row.enabled !== false && !row.calculation),
  columns: options.columns.filter((column) => column.enabled !== false && !column.calculation),
});

const getFieldName = (rowKey: string, columnKey: string) => `${rowKey}${columnKey}`;
const getRowHeaderFieldName = (rowKey: string) => `${rowKey}__rowHeader`;
const hasRowHeader = (options: DynamicTableExcelOptions) => options.showRowHeader !== false;

const getRowLabel = (options: DynamicTableExcelOptions, row: DynamicTableRowConfig) =>
  options.continuous && !row.calculation
    ? (options.values[getRowHeaderFieldName(row.key)] ?? '')
    : row.label;

const getExcelValue = (
  options: DynamicTableExcelOptions,
  row: DynamicTableRowConfig,
  column: DynamicTableColumnConfig,
) => {
  const value = options.values[getFieldName(row.key, column.key)];
  if (value === undefined || value === '') return null;
  if (column.inputType === 'text' || column.inputType === 'select') return value;
  if (column.inputType === 'duration' && column.duration) {
    const unit =
      options.values[getFieldName(row.key, column.duration.unitKey)] || column.duration.defaultUnit;
    return `${value} ${unit}`;
  }
  return Number(value);
};

export const getDynamicTableWorksheetName = (title: string) =>
  title
    .replace(/[\\/?*[\]:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 31);

const getDynamicTableWorksheetNames = (tables: DynamicTableExcelOptions[]) => {
  const usedNames = new Set<string>();
  return tables.map((table, index) => {
    const baseName = getDynamicTableWorksheetName(table.title) || `Table ${index + 1}`;
    let name = baseName;
    let suffixNumber = 2;
    while (usedNames.has(name.toLowerCase())) {
      const suffix = ` (${suffixNumber})`;
      name = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
      suffixNumber += 1;
    }
    usedNames.add(name.toLowerCase());
    return name;
  });
};

const getGuidance = (
  columns: DynamicTableColumnConfig[],
  interpolationValues: Record<string, unknown>,
) =>
  columns.map((column) => {
    const guidance = interpolateQuestionText(column.hint, interpolationValues) ?? '';
    if (column.inputType !== 'duration') return guidance;
    return [guidance, 'Enter a number and unit, for example 3 months.'].filter(Boolean).join(' ');
  });

const getExpectedRows = (options: DynamicTableExcelOptions) => {
  const rowHeader = hasRowHeader(options);
  return [
    [options.title],
    [],
    [...(rowHeader ? [options.rowHeader] : []), ...options.columns.map((column) => column.label)],
    [
      ...(rowHeader ? ['Guidance'] : []),
      ...getGuidance(options.columns, options.interpolationValues),
    ],
    ...options.rows.map((row) => (rowHeader ? [getRowLabel(options, row)] : [])),
  ];
};

const addDynamicTableWorksheet = (
  workbook: Workbook,
  worksheetName: string,
  options: DynamicTableExcelOptions,
) => {
  const rowHeader = hasRowHeader(options);
  const worksheet = workbook.addWorksheet(worksheetName, {
    views: [
      {
        state: 'frozen',
        xSplit: rowHeader ? 1 : 0,
        ySplit: 4,
        topLeftCell: rowHeader ? 'B5' : 'A5',
        activeCell: rowHeader ? 'B5' : 'A5',
      },
    ],
  });
  const guidance = getGuidance(options.columns, options.interpolationValues);

  worksheet.addRow([options.title]);
  worksheet.addRow([]);
  worksheet.addRow([
    ...(rowHeader ? [options.rowHeader] : []),
    ...options.columns.map((column) => column.label),
  ]);
  worksheet.addRow([...(rowHeader ? ['Guidance'] : []), ...guidance]);
  options.rows.forEach((row) => {
    worksheet.addRow([
      ...(rowHeader ? [getRowLabel(options, row)] : []),
      ...options.columns.map((column) => getExcelValue(options, row, column)),
    ]);
  });

  worksheet.columns = [
    ...(rowHeader
      ? [
          {
            width: Math.min(
              60,
              Math.max(
                options.rowHeader.length,
                'Guidance'.length,
                ...options.rows.map((row) => getRowLabel(options, row).length),
              ) + 2,
            ),
          },
        ]
      : []),
    ...options.columns.map((column, index) => ({
      width: Math.min(60, Math.max(12, column.label.length, guidance[index].length) + 2),
    })),
  ];

  const finalColumn = options.columns.length + (rowHeader ? 1 : 0);
  const finalRow = options.rows.length + 4;
  worksheet.getRow(1).font = { bold: true, size: 16 };

  for (let columnNumber = 1; columnNumber <= finalColumn; columnNumber += 1) {
    const headerCell = worksheet.getRow(3).getCell(columnNumber);
    headerCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
    headerCell.font = { bold: true };
    headerCell.alignment = { vertical: 'middle', wrapText: true };
  }

  if (rowHeader) {
    for (let rowNumber = 4; rowNumber <= finalRow; rowNumber += 1) {
      const rowHeaderCell = worksheet.getRow(rowNumber).getCell(1);
      rowHeaderCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: ROW_HEADER_FILL },
      };
      rowHeaderCell.font = { bold: true };
      rowHeaderCell.alignment = { vertical: 'middle', wrapText: true };
    }
  }

  const dataColumnOffset = rowHeader ? 2 : 1;
  options.columns.forEach((column, columnIndex) => {
    if (column.inputType !== 'select' || !column.options?.length) return;
    if (column.options.some((option) => /[,"]/.test(option))) return;
    const list = column.options.join(',');
    if (list.length > 253) return;
    for (let rowNumber = 5; rowNumber <= finalRow; rowNumber += 1) {
      worksheet.getRow(rowNumber).getCell(columnIndex + dataColumnOffset).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${list}"`],
      };
    }
  });

  for (let rowNumber = 3; rowNumber <= finalRow; rowNumber += 1) {
    for (let columnNumber = 1; columnNumber <= finalColumn; columnNumber += 1) {
      const cell = worksheet.getRow(rowNumber).getCell(columnNumber);
      cell.border = {
        top: { style: 'thin', color: { argb: BORDER_COLOUR } },
        left: { style: 'thin', color: { argb: BORDER_COLOUR } },
        bottom: { style: 'thin', color: { argb: BORDER_COLOUR } },
        right: { style: 'thin', color: { argb: BORDER_COLOUR } },
      };
      cell.alignment = {
        ...cell.alignment,
        vertical: 'top',
        wrapText: true,
      };
    }
  }

  return worksheet;
};

export const buildDynamicTablesWorkbook = async ({ title, tables }: DynamicTablesExcelOptions) => {
  const { Workbook: ExcelWorkbook } = await import('exceljs');
  const workbook = new ExcelWorkbook();
  workbook.creator = 'GRID Forms';
  workbook.created = new Date();
  workbook.title = title;
  const editableTables = tables.map(withEditableCellsOnly);
  const worksheetNames = getDynamicTableWorksheetNames(editableTables);
  editableTables.forEach((table, index) =>
    addDynamicTableWorksheet(workbook, worksheetNames[index], table),
  );
  return workbook;
};

export const buildDynamicTableWorkbook = async (options: DynamicTableExcelOptions) => {
  return buildDynamicTablesWorkbook({ title: options.title, tables: [options] });
};

const downloadBuffer = (buffer: ArrayBuffer, fileName: string) => {
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const downloadDynamicTableAsExcel = async (options: DynamicTableExcelOptions) => {
  return downloadDynamicTablesAsExcel({ title: options.title, tables: [options] });
};

export const downloadDynamicTablesAsExcel = async (options: DynamicTablesExcelOptions) => {
  const workbook = await buildDynamicTablesWorkbook(options);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer as ArrayBuffer, getExcelTemplateFilename(options.title));
};

const getCellScalar = (value: unknown): string | number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  throw new Error('The spreadsheet contains a formula or unsupported cell value.');
};

const getComparableCellText = (value: unknown) => {
  const scalar = getCellScalar(value);
  return scalar === null ? '' : String(scalar).trim();
};

const isBlankCell = (value: unknown) => value === null || value === undefined || value === '';

const assertIdenticalLayout = (
  worksheet: import('exceljs').Worksheet,
  options: DynamicTableExcelOptions,
) => {
  const expectedRows = getExpectedRows(options);
  const expectedRowCount = options.rows.length + 4;
  const expectedColumnCount = options.columns.length + (hasRowHeader(options) ? 1 : 0);
  const checkedRowCount = options.continuous ? 4 : expectedRowCount;

  for (let rowNumber = 1; rowNumber <= checkedRowCount; rowNumber += 1) {
    const expectedRow = expectedRows[rowNumber - 1] ?? [];
    for (let columnNumber = 1; columnNumber <= expectedColumnCount; columnNumber += 1) {
      const expected = expectedRow[columnNumber - 1];
      if (expected === undefined) continue;
      const actual = getComparableCellText(worksheet.getRow(rowNumber).getCell(columnNumber).value);
      if (actual !== String(expected).trim()) {
        throw new Error(
          'This file does not have the same table layout as the downloaded template.',
        );
      }
    }
  }

  const firstDataRow = 5;
  let lastDataRow = expectedRowCount;
  if (options.continuous) {
    const calculatedRows = options.rows.filter((row) => Boolean(row.calculation));
    const firstCalculatedRow = worksheet.rowCount - calculatedRows.length + 1;
    if (calculatedRows.length > 0 && firstCalculatedRow < firstDataRow) {
      throw new Error('This file does not have the same table layout as the downloaded template.');
    }
    calculatedRows.forEach((row, index) => {
      if (!hasRowHeader(options)) return;
      const actual = getComparableCellText(
        worksheet.getRow(firstCalculatedRow + index).getCell(1).value,
      );
      if (actual !== row.label.trim()) {
        throw new Error(
          'This file does not have the same table layout as the downloaded template.',
        );
      }
    });
    lastDataRow = calculatedRows.length > 0 ? firstCalculatedRow - 1 : worksheet.rowCount;
  }

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    row.eachCell({ includeEmpty: false }, (cell, columnNumber) => {
      if (
        ((!options.continuous && rowNumber > expectedRowCount) ||
          columnNumber > expectedColumnCount) &&
        !isBlankCell(cell.value)
      ) {
        throw new Error(
          'This file does not have the same table layout as the downloaded template.',
        );
      }
    });
  });

  return { firstDataRow, lastDataRow };
};

const parseNumberCell = (
  value: unknown,
  row: DynamicTableRowConfig,
  column: DynamicTableColumnConfig,
) => {
  const scalar = getCellScalar(value);
  if (scalar === null) return '';
  const text = String(scalar).trim();
  const number = Number(text);
  if (!Number.isFinite(number)) {
    throw new Error(`Enter a number for ${row.label}, ${column.label}.`);
  }
  if (row.allowNegativeNumbers !== true && column.allowNegativeNumbers !== true && number < 0) {
    throw new Error(`Enter 0 or a positive number for ${row.label}, ${column.label}.`);
  }
  if (row.allowDecimal !== true && column.allowDecimal !== true && !Number.isInteger(number)) {
    throw new Error(`Enter a whole number for ${row.label}, ${column.label}.`);
  }
  return String(number);
};

const parseSelectCell = (
  value: unknown,
  row: DynamicTableRowConfig,
  column: DynamicTableColumnConfig,
) => {
  const text = getComparableCellText(value);
  if (!text) return '';
  if (!column.options?.includes(text)) {
    throw new Error(`Select a valid value for ${row.label}, ${column.label}.`);
  }
  return text;
};

const parseDurationCell = (
  value: unknown,
  row: DynamicTableRowConfig,
  column: DynamicTableColumnConfig,
) => {
  const scalar = getCellScalar(value);
  if (scalar === null || !column.duration) return { duration: '', unit: '' };
  const text = String(scalar).trim();
  const match = text.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+))(?:\s+(.+))?$/);
  if (!match) {
    throw new Error(`Enter a number and unit for ${row.label}, ${column.label}.`);
  }

  const duration = parseNumberCell(match[1], row, column);
  const enteredUnit = match[2]?.trim().toLowerCase();
  const unit = enteredUnit
    ? column.duration.units.find((option) => {
        const configuredUnit = option.value.toLowerCase();
        const singularUnit = configuredUnit.replace(/\(s\)$/i, '');
        return (
          enteredUnit === configuredUnit ||
          enteredUnit === singularUnit ||
          enteredUnit === `${singularUnit}s`
        );
      })?.value
    : column.duration.defaultUnit;
  if (!unit) {
    throw new Error(`Select a valid unit for ${row.label}, ${column.label}.`);
  }
  return { duration, unit };
};

const importColumnValue = (
  importedValues: Record<string, string>,
  row: DynamicTableRowConfig,
  column: DynamicTableColumnConfig,
  value: unknown,
) => {
  const field = getFieldName(row.key, column.key);
  if (column.inputType === 'text') {
    importedValues[field] = getComparableCellText(value);
    return;
  }
  if (column.inputType === 'select') {
    importedValues[field] = parseSelectCell(value, row, column);
    return;
  }
  if (column.inputType === 'duration' && column.duration) {
    const parsed = parseDurationCell(value, row, column);
    importedValues[field] = parsed.duration;
    importedValues[getFieldName(row.key, column.duration.unitKey)] = parsed.unit;
    return;
  }
  importedValues[field] = parseNumberCell(value, row, column);
};

const importDynamicTableWorksheet = (
  worksheet: import('exceljs').Worksheet,
  options: DynamicTableExcelOptions,
) => {
  const { firstDataRow, lastDataRow } = assertIdenticalLayout(worksheet, options);
  const importedValues: Record<string, string> = {};
  const rowHeader = hasRowHeader(options);
  const dataColumnOffset = rowHeader ? 2 : 1;
  if (options.continuous) {
    let importedRowIndex = 0;
    for (let worksheetRow = firstDataRow; worksheetRow <= lastDataRow; worksheetRow += 1) {
      const excelRow = worksheet.getRow(worksheetRow);
      const rowValues = Array.from(
        { length: options.columns.length + (rowHeader ? 1 : 0) },
        (_, index) => getComparableCellText(excelRow.getCell(index + 1).value),
      );
      if (rowValues.every((value) => value === '')) continue;

      const rowLabel = rowHeader ? rowValues[0] : String(importedRowIndex + 1);
      if (rowHeader && !rowLabel) {
        throw new Error(`Enter a value for ${options.rowHeader}.`);
      }
      if (
        rowHeader &&
        options.rowHeaderInput?.inputType === 'select' &&
        !(options.rowHeaderInput.dropdownOptions ?? options.rowHeaderInput.options)?.includes(
          rowLabel,
        )
      ) {
        throw new Error(`Select a valid value for ${options.rowHeader}.`);
      }

      importedRowIndex += 1;
      const row: DynamicTableRowConfig = {
        key: `continuousRow${importedRowIndex}`,
        label: rowLabel,
      };
      if (rowHeader) importedValues[getRowHeaderFieldName(row.key)] = rowLabel;
      options.columns.forEach((column, columnIndex) => {
        if (column.calculation || column.enabled === false) return;
        const value = excelRow.getCell(columnIndex + dataColumnOffset).value;
        importColumnValue(importedValues, row, column, value);
      });
    }
    return importedValues;
  }

  options.rows.forEach((row, rowIndex) => {
    options.columns.forEach((column, columnIndex) => {
      if (
        row.calculation ||
        row.enabled === false ||
        column.calculation ||
        column.enabled === false
      ) {
        return;
      }
      const value = worksheet.getRow(rowIndex + 5).getCell(columnIndex + dataColumnOffset).value;
      importColumnValue(importedValues, row, column, value);
    });
  });
  return importedValues;
};

export const importDynamicTablesFromExcel = async (
  file: File,
  options: DynamicTablesExcelOptions,
): Promise<Record<string, string>> => {
  const importedTables = await importDynamicTableSheetsFromExcel(file, options);
  return Object.assign({}, ...importedTables);
};

export const importDynamicTableSheetsFromExcel = async (
  file: File,
  options: DynamicTablesExcelOptions,
): Promise<Record<string, string>[]> => {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    throw new Error('Select an XLSX file.');
  }

  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch (_error) {
    throw new Error('The XLSX file could not be opened.');
  }

  if (workbook.worksheets.length !== options.tables.length) {
    throw new Error('This file does not have the same table layout as the downloaded template.');
  }
  const editableTables = options.tables.map(withEditableCellsOnly);
  const worksheetNames = getDynamicTableWorksheetNames(editableTables);
  return editableTables.map((table, index) => {
    const worksheet = workbook.worksheets[index];
    if (worksheet.name !== worksheetNames[index]) {
      throw new Error('This file does not have the same table layout as the downloaded template.');
    }
    return importDynamicTableWorksheet(worksheet, table);
  });
};

export const importDynamicTableFromExcel = async (
  file: File,
  options: DynamicTableExcelOptions,
): Promise<Record<string, string>> =>
  importDynamicTablesFromExcel(file, { title: options.title, tables: [options] });
